import { useState } from 'react'
import { Link } from 'react-router-dom'
import SEO from '../components/SEO'
import SchemaMarkup from '../components/SchemaMarkup'

const faqs = [
  {
    q: 'What is the process?',
    a: 'Depending on your request, we gather all the requirements as soon as possible and define the ideal approach for your product engineering or staff augmentation roadmap.',
  },
  {
    q: 'Will the team be experienced enough?',
    a: 'Yes. MindVista engages engineers with proven delivery experience and strong technical communication, so collaboration stays smooth from discovery to release.',
  },
  {
    q: 'How long does a typical project take?',
    a: 'Timelines depend on scope and complexity, but we prioritize fast iteration cycles with clear milestones so value is delivered early and consistently.',
  },
  {
    q: 'How do we get started and how much does it cost?',
    a: 'We begin with a discovery call to understand goals and constraints, then provide a transparent proposal with timeline, team shape, and pricing model.',
  },
]

const services = [
  {
    title: 'Web Development',
    description: 'Building responsive, modern websites and web applications with scalable architecture.',
    icon: '</>',
    primary: true,
    path: '/services/web-development',
  },
  {
    title: 'AI/ML Engineering',
    description: 'Intelligent systems, automation, and analytics models for measurable business impact.',
    icon: 'AI',
    path: '/services/ai-ml-engineer',
  },
  {
    title: 'Mobile Apps',
    description: 'Cross-platform and native mobile products optimized for growth and retention.',
    icon: 'APP',
    path: '/services/mobile-apps',
  },
  {
    title: 'AI Automation',
    description: 'Workflow automation and AI-powered operations designed to reduce manual load.',
    icon: 'AUTO',
    path: '/services/ai-automation',
  },
]

const testimonialCards = [
  {
    quote:
      'MindVista transformed our outdated systems into a cutting-edge digital platform. Their expertise and dedication significantly boosted our efficiency.',
    name: 'Jane Smith',
    role: 'CEO, Tech Innovators',
  },
  {
    quote:
      'Working with MindVista has been a game-changer for our company. Their ability to understand our needs and execute fast is outstanding.',
    name: 'Tom Williams',
    role: 'Founder, Creative Solutions',
  },
  {
    quote:
      'From consultation to implementation, MindVista delivered with precision. We saw immediate improvements in operations and product quality.',
    name: 'Michael Brown',
    role: 'Marketing Director, PrimeTech',
  },
]

const logos = [
  '/images/home/logos/amazon-logo.png',
  '/images/home/logos/google-logo.png',
  '/images/home/logos/microsoft-logo.png',
  '/images/home/logos/stripe-logo.png',
  '/images/home/logos/shopify-logo.png',
  '/images/home/logos/coursera-logo.png',
  '/images/home/logos/wordpress.png',
  '/images/home/logos/bootstrap.png',
]

const Home = () => {
  const [openFaq, setOpenFaq] = useState(0)
  const [aboutServiceIndex, setAboutServiceIndex] = useState(0)
  const activeAboutService = services[aboutServiceIndex]

  const handlePrevAboutService = () => {
    setAboutServiceIndex((prev) => (prev === 0 ? services.length - 1 : prev - 1))
  }

  const handleNextAboutService = () => {
    setAboutServiceIndex((prev) => (prev === services.length - 1 ? 0 : prev + 1))
  }

  return (
    <div className="home-yellow-leaks">
      <SEO 
        title="Home" 
        description="MindVista offers cutting-edge web development, mobile app development, AI/ML engineering, and branding." 
        keywords="web development, digital agency" 
      />
      <SchemaMarkup />
      <section className="hero-inspo-bg relative min-h-[92vh] overflow-hidden">
        <div className="hero-inspo-grid" />
        <div className="hero-inspo-streak" />
        <div className="hero-inspo-vignette" />

        <div className="container-main relative z-10 flex min-h-[92vh] items-center justify-center overflow-hidden text-center">
          <div className="pointer-events-none absolute inset-x-12 top-[16%] h-[62%] bg-[radial-gradient(45%_38%_at_22%_56%,rgba(249,193,37,0.24),transparent_72%),radial-gradient(48%_36%_at_78%_40%,rgba(249,193,37,0.18),transparent_70%),radial-gradient(42%_32%_at_52%_72%,rgba(249,193,37,0.14),transparent_74%)] blur-3xl" />

          <div className="relative z-10 flex flex-col items-center">
            <span className="section-kicker mb-6">MindVista</span>
            <h1 className="max-w-5xl text-4xl font-bold leading-[1.2] text-white md:text-7xl md:leading-[1.15]">
              Your <span className="accent-text">Comprehensive</span> Solution For
              <br />
              Online Busi<span className="accent-text">ness</span>
            </h1>
            <p className="mt-6 max-w-3xl text-lg text-[#d0d0d0] md:text-2xl">
              Creating innovative solutions to make business operations more efficient through cutting-edge technology and creative strategies
            </p>
            <div className="mt-10 flex flex-wrap justify-center gap-3">
              <Link to="/contact" className="btn-primary">Book a Free Call</Link>
              <Link to="/services" className="btn-outline">Explore Services</Link>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20">
        <div className="container-main">
          <div className="mb-10 text-center">
            <span className="section-kicker">Services</span>
            <h2 className="mt-4 text-4xl font-bold md:text-6xl">Your product engineering team is here</h2>
          </div>

          <div className="grid gap-5 md:grid-cols-2">
            {services.map((service) => (
              <Link
                key={service.title}
                to={service.path}
                className={`rounded-3xl p-7 md:p-9 transition-all duration-300 ${service.primary ? 'service-card-primary' : 'service-card-secondary'}`}
              >
                <div className={`mb-6 inline-flex h-12 w-12 items-center justify-center rounded-xl ${service.primary ? 'bg-black/12 text-black' : 'bg-white/10 text-[rgb(249,193,37)]'}`}>
                  <span className="text-sm font-bold">{service.icon}</span>
                </div>
                <h3 className={`text-3xl font-semibold ${service.primary ? 'text-black' : 'text-white'}`}>{service.title}</h3>
                <p className={`mt-3 text-base ${service.primary ? 'text-black/75' : 'text-[#acacac]'}`}>{service.description}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20">
        <div className="container-main grid gap-8 lg:grid-cols-2">
          <div className="neo-card p-8 md:p-10">
            <span className="section-kicker">About Us</span>
            <h2 className="mt-4 text-3xl font-semibold md:text-5xl">Built on creativity, collaboration, and trust</h2>
            <p className="mt-4 text-base leading-relaxed text-[#9d9d9d] md:text-lg">
              At MindVista, we transform business ideas into practical digital products through transparent communication, engineering excellence, and consistent delivery.
            </p>
            <Link to="/about" className="btn-outline mt-7">Learn More</Link>
          </div>

          <div className="neo-card relative overflow-hidden p-8 md:p-10">
            <video autoPlay muted loop playsInline className="absolute inset-0 h-full w-full object-cover opacity-25">
              <source src="/images/home/acheivements.mp4" type="video/mp4" />
            </video>
            <div className="relative">
              <span className="section-kicker">Achievements</span>
              <div className="mt-7 grid grid-cols-2 gap-4">
                {[
                  { value: '5+', label: 'Years' },
                  { value: '100+', label: 'Projects' },
                  { value: '20+', label: 'Awards' },
                  { value: '99%', label: 'Success' },
                ].map((item) => (
                  <div key={item.label} className="rounded-2xl border border-white/10 bg-black/40 p-5 text-center">
                    <p className="text-4xl font-bold text-[rgb(249,193,37)]">{item.value}</p>
                    <p className="mt-1 text-sm text-[#d5d5d5]">{item.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="faq" className="py-20">
        <div className="container-main grid gap-10 lg:grid-cols-[0.9fr_1.1fr]">
          <div>
            <h2 className="text-4xl font-semibold md:text-6xl">Working with us is simple as that</h2>
            <p className="mt-5 text-lg text-[#adadad]">Didn&apos;t find what you were looking for? Let&apos;s talk.</p>
            <Link to="/contact" className="btn-primary mt-8">Contact us</Link>
          </div>

          <div className="neo-card p-6 md:p-8">
            {faqs.map((item, idx) => (
              <div className="faq-item" key={item.q}>
                <button
                  type="button"
                  onClick={() => setOpenFaq((prev) => (prev === idx ? -1 : idx))}
                  className="flex w-full items-center justify-between gap-3 text-left"
                >
                  <span className="text-xl font-medium text-white">{item.q}</span>
                  <span className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-white/25 text-2xl text-white">
                    {openFaq === idx ? '-' : '+'}
                  </span>
                </button>
                <div className={`faq-answer ${openFaq === idx ? 'open' : ''}`}>
                  <p className="pt-3 text-base leading-relaxed text-[#a8a8a8]">{item.a}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20">
        <div className="container-main">
          <div className="mb-8 flex flex-wrap items-start justify-between gap-5">
            <div className="max-w-4xl">
              <span className="section-kicker">About Us</span>
              <h2 className="mt-4 text-4xl font-semibold md:text-6xl">Driving Business Success Through Innovation</h2>
              <p className="mt-4 text-lg text-[#acacac]">
                At MindVista, we offer a comprehensive range of IT services designed to meet the unique needs of our clients. Our expert team leverages the latest technologies and creative strategies to deliver high-quality solutions that drive efficiency and success.
              </p>
            </div>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={handlePrevAboutService}
                aria-label="Previous service card"
                className="inline-flex h-12 w-12 items-center justify-center rounded-full border border-[rgb(249,193,37)]/70 bg-transparent text-lg text-[rgb(249,193,37)] hover:bg-[rgb(249,193,37)]/14 hover:text-white"
              >
                &#8249;
              </button>
              <button
                type="button"
                onClick={handleNextAboutService}
                aria-label="Next service card"
                className="inline-flex h-12 w-12 items-center justify-center rounded-full border border-[rgb(249,193,37)]/70 bg-transparent text-lg text-[rgb(249,193,37)] hover:bg-[rgb(249,193,37)]/14 hover:text-white"
              >
                &#8250;
              </button>
            </div>
          </div>

          <article className="neo-card relative overflow-hidden rounded-3xl p-7 md:p-10">
            <div className="absolute inset-0 bg-gradient-to-r from-[rgb(249,193,37)]/10 via-transparent to-black/25" />
            <div className="relative z-10 grid gap-8 md:grid-cols-[1.2fr_0.8fr] md:items-end">
              <div>
                <div className="mb-4 inline-flex h-11 w-11 items-center justify-center rounded-xl border border-[rgb(249,193,37)]/40 bg-[rgb(249,193,37)]/12 text-[rgb(249,193,37)]">
                  <span className="text-xs font-bold">{activeAboutService.icon}</span>
                </div>
                <h3 className="text-4xl font-semibold text-white md:text-5xl">{activeAboutService.title}</h3>
                <p className="mt-4 max-w-3xl text-lg leading-relaxed text-[#b6b6b6]">{activeAboutService.description}</p>
                <Link to={activeAboutService.path} className="mt-7 inline-flex items-center gap-2 text-base font-semibold text-[rgb(249,193,37)] hover:text-white">
                  View More
                  <span aria-hidden="true">&rarr;</span>
                </Link>
              </div>
              <div className="rounded-2xl border border-white/10 bg-black/30 p-5">
                <p className="text-xs uppercase tracking-[0.15em] text-[#9f9f9f]">Service {aboutServiceIndex + 1} of {services.length}</p>
                <div className="mt-4 space-y-3">
                  {services.map((service, index) => (
                    <button
                      key={service.title}
                      type="button"
                      onClick={() => setAboutServiceIndex(index)}
                      className={`flex w-full items-center justify-between rounded-xl border px-3 py-2 text-left text-sm ${
                        index === aboutServiceIndex
                          ? 'border-[rgb(249,193,37)]/45 bg-[rgb(249,193,37)]/12 text-white'
                          : 'border-white/10 bg-black/20 text-[#9d9d9d] hover:border-[rgb(249,193,37)]/30 hover:text-white'
                      }`}
                    >
                      <span>{service.title}</span>
                      <span className="text-[rgb(249,193,37)]">{index === aboutServiceIndex ? 'Active' : 'Open'}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </article>
        </div>
      </section>

      <section className="py-20">
        <div className="container-main">
          <div className="mb-8 text-center">
            <span className="section-kicker">Testimonials</span>
            <h2 className="mt-4 text-4xl font-semibold md:text-5xl">What our users are saying</h2>
          </div>

          <div className="grid gap-4 md:grid-cols-3">
            {testimonialCards.map((item) => (
              <article key={item.name} className="neo-card p-6">
                <p className="text-sm leading-relaxed text-[#d0d0d0]">“{item.quote}”</p>
                <p className="mt-6 font-semibold text-white">{item.name}</p>
                <p className="text-sm text-[#9b9b9b]">{item.role}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16">
        <div className="container-main overflow-hidden">
          <div className="logo-track">
            {[...logos, ...logos].map((logo, index) => (
              <div key={`${logo}-${index}`} className="flex h-14 min-w-[140px] items-center justify-center rounded-xl border border-white/10 bg-[#141414] px-4">
                <img src={logo} alt="partner" className="max-h-7 w-auto opacity-80" />
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20">
        <div className="container-main">
          <div className="glass-card rounded-3xl p-7 md:p-10">
            <h2 className="text-4xl font-semibold md:text-6xl">Let&apos;s talk</h2>
            <p className="mt-3 text-lg text-[#b9b9b9]">Tell us about your need and we will help.</p>

            <form className="mt-8 grid gap-5 lg:grid-cols-2">
              <div className="space-y-4">
                <div>
                  <label className="mb-2 block text-sm font-medium text-white">Full name *</label>
                  <input type="text" className="input-dark" placeholder="Your name" />
                </div>
                <div>
                  <label className="mb-2 block text-sm font-medium text-white">Email *</label>
                  <input type="email" className="input-dark" placeholder="Your email" />
                </div>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="mb-2 block text-sm font-medium text-white">Tell us how we can help</label>
                  <textarea rows={5} className="input-dark" placeholder="Your message" />
                </div>
                <p className="text-sm text-[#9d9d9d]">Your personal data is processed in accordance with our Privacy Notice.</p>
                <div className="flex justify-end">
                  <button type="submit" className="btn-primary">Submit</button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </section>
    </div>
  )
}

export default Home

